<template>
  <div class="cbt">
    <h1 class="hidden">全国一斉CBT模擬試験 総合成績</h1>
    <section class="h2Wrap">
      <h2>成績発表</h2>
      <div class="u-containar-md">
        <div class="pageTitle">総合成績</div>
        <CbtLatestScore :score="result.score_new" :maxScore="140" />
        <section class="h3Wrap">
          <h3>成績の推移</h3>
          <div class="chart">
            <CbtAllResultChart
              :latestTestScore="result.latestScore"
              :firstTestScore="result.firstScore"
              :testDates="result.testDates"
              :numberOfTests="result.numberOfTests"
            />
          </div>
          <div class="caption">
            ※ 第五項のグラフは、実際の点数を1.5倍した数値を反映しています。
          </div>
        </section>
        <section class="h3Wrap">
          <h3>初回の成績</h3>
          <p class="captionText">
            下記のデータは、初回の点数を基にして算出したものです。
          </p>
          <div class="caption">
            ※ 2回目以降の点数は、順位や平均点には反映されません
          </div>
          <ul class="score">
            <li>
              <div class="head">あなたの得点</div>
              <div class="body">
                <span>{{ result.score }}<span class="unit">点</span></span> /
                140点
              </div>
            </li>
            <li>
              <div class="head">総受験者数</div>
              <div class="body">
                <span>{{ result.total }}<span class="unit">人</span></span>
              </div>
            </li>
            <li>
              <div class="head">全受験者の平均点</div>
              <div class="body">
                <span>{{ result.average }}<span class="unit">点</span></span> /
                140点
              </div>
            </li>
            <li>
              <div class="head">あなたの全国順位</div>
              <div class="body">
                <span>{{ result.ranking }}<span class="unit">位</span></span> /
                {{ result.total }}人中
              </div>
            </li>
            <li>
              <div class="head">あなたの大学内での順位</div>
              <div class="body">
                <span>{{ result.u_ranking }}<span class="unit">位</span></span>
                / {{ result.u_total }}人中
              </div>
            </li>
          </ul>
        </section>
      </div>
      <div class="btnArea">
        <Button
          :label="'CBT模擬試験トップへ戻る'"
          :handleClick="() => $router.push('/magazines/exam/cbt')"
        />
      </div>
    </section>
    <section class="h2Wrap">
      <h2>前回の成績・解答</h2>
    </section>
    <div class="u-containar-md">
      <ul class="cards">
        <CbtTermCard
          v-for="(item, index) in terms"
          :key="index"
          :data="item"
          :number="index + 1"
        />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useStore } from 'vuex'
import { axiosSetting } from '@/utils/axiosSetting'
import Button from '@/components/elements/Button.vue'
import CbtTermCard from '@/components/block/CbtTermCard.vue'
import CbtAllResultChart from '@/components/block/CbtAllResultChart.vue'
import CbtLatestScore from '@/components/block/CbtLatestScore.vue'

const parseTestScore = (graphDataString: string) => {
  if (graphDataString.length === 0) return []
  const array = graphDataString.split(',').map(Number)
  // 第五項は1.5倍
  array[4] = array[4] * 1.5
  return array
}
const parseTestDate = (graphDateString: string) => {
  if (graphDateString.length === 0) return []
  const dateArray = graphDateString.split(',').map((date) => date.split(' ')[0])
  return dateArray
}
const getTestDates = (dateStringArray: string[]) => {
  const graphDates = dateStringArray.map((dateString) =>
    parseTestDate(dateString)
  )
  return graphDates
}

export default defineComponent({
  name: 'CbtAllResult',
  components: {
    Button,
    CbtTermCard,
    CbtAllResultChart,
    CbtLatestScore
  },
  setup() {
    const store = useStore()
    const result = ref({})
    const ajax = axiosSetting('app', 'action')
    const params = {
      users_id: store.state.userInfo.zip,
      year: '2022'
    }

    const getData = async () => {
      const data = await ajax
        .post('/api/v1/cbt_results', params)
        .then((res: any) => {
          return res.data
        })
      data.firstScore = parseTestScore(data.graph)
      data.latestScore = parseTestScore(data.graph_new)
      data.numberOfTests = data.past_performance.length
      data.testDates =
        data.numberOfTests > 1
          ? getTestDates([data.graph_date, data.graph_new_date])
          : getTestDates([data.graph_date])
      result.value = data
    }
    getData()

    const terms = [
      {
        title: '基本事項 / 医学一般(30問)'
      },
      {
        title: '人体各器官の正常構造と機能、病態、診断、治療(30問)'
      },
      {
        title: '全身に及ぶ生理的変化、病態、診断、治療(30問)'
      },
      {
        title: '診療の基本 / 社会と医学・医療(30問)'
      },
      {
        title: '4連問特集(20問)'
      }
    ]

    return {
      result,
      terms
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.cbt {
  letter-spacing: 0.03em;
}

.h2Wrap {
  margin-top: 40px;
  & + & {
    margin-top: 64px;
  }

  h2 {
    background-color: var.$color-sub-bg-g;
    padding: 10px 16px;
    font-size: 2rem;
    line-height: 1.6;
    font-weight: 500;
    @include mixins.media() {
      width: 712px;
      margin-right: auto;
      margin-left: auto;
    }
  }
}

.h3Wrap {
  margin-top: 24px;
}

.pageTitle {
  margin-top: 24px;
  font-weight: 500;
  font-size: 1.8rem;
  line-height: 1.34;
  padding-left: 9px;
  border-left: 4px solid var.$color-main;
}

h3 {
  margin-top: 32px;
  background-color: var.$color-main;
  color: #fff;
  font-size: 1.6rem;
  padding: 2px 16px 4px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0.03em;
  @include mixins.media() {
    margin-top: 40px;
  }
}

.score {
  margin-top: 24px;
  font-weight: 500;
  line-height: 1.7;
  li {
    padding-bottom: 11px;
    border-bottom: 1px solid var.$color-border-gray;
    @include mixins.media() {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 16px;
    }
    + li {
      margin-top: 20px;
      @include mixins.media() {
        margin-top: 16px;
      }
    }
    .body {
      font-size: 1.6rem;
      span {
        color: var.$color-main-text;
        font-size: 2.4rem;
        &.unit {
          font-size: 1.6rem;
        }
      }
    }
  }
}

.captionText {
  margin-top: 16px;
  letter-spacing: 0.03em;
}
.caption {
  margin-top: 12px;
  font-size: 1.2rem;
  line-height: 1.5;
}

.graph {
  margin-top: 24px;
  height: 200px;
  background-color: #ddd;
}

.btnArea {
  margin-top: 32px;
  background-color: var.$color-other-bg;
  padding: 24px;
  @include mixins.media() {
    margin-top: 40px;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: center;
    button {
      margin-right: 0;
      margin-left: 0;
    }
  }
  button + button {
    margin-top: 16px;
    @include mixins.media() {
      margin-top: 0;
      margin-left: 16px;
    }
  }
}

.cards {
  padding-top: 24px;
  li + li {
    margin-top: 16px;
    @include mixins.media() {
      margin-top: 32px;
    }
  }
}

.chart {
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  background-color: #f0f0f0;
}
</style>
